import React from "react";

import {
  callAnswered,
  callEnded,
  supervise as superviseApi,
} from "~/services/calling/sidecarApi";
import { linkRecentOutboundCallToCustomer } from "~/services/callService";
import {
  buildSipUri,
  deriveConferenceRoom,
} from "~/services/calling/config";
import { AppSocket } from "~/services/calling/socketClient";
import { SipPhone } from "~/services/calling/sipPhone";
import { SipNotReadyError } from "~/services/calling/sipErrors";
import type {
  AgentPresence,
  SipConnectLoggingContext,
  PendingSupervise,
  SuperviseMode,
} from "~/services/calling/types";
import type { CallState } from "~/context/call/callState";
import { PENDING_BARGE_TRANSFER_WINDOW_MS } from "~/context/call/callState";
import { preparePendingSupervise } from "~/context/call/superviseAutoAccept";
import {
  buildConsultSlotSnapshot,
  buildHoldSlotSnapshot,
  buildSwappedConsultSlots,
  clearConsultSlotFields,
  isConsultDialBlocked,
  isDialingFromHeldCall,
} from "~/context/call/consultState";
import {
  buildOutboundTarget,
  collectIncomingCallCorrelators,
  formatOutboundCallErrorMessage,
  matchesQueueUniqueId,
  requireSipRegistered,
} from "~/context/call/helpers";
import type { IncomingCallInfo } from "~/services/calling/types";
import {
  resolveOutboundLinkCustomerId,
  type OutboundDialOptions,
} from "~/context/call/outboundCallLink";

const shouldLinkOutboundCallLog = (target: string) => {
  const normalized = target.trim();
  if (!normalized) return false;
  // Feature codes (e.g. *43 call timer) are not customer/PSTN outbound legs.
  return !normalized.startsWith("*");
};

const resolveSelectedIncomingCall = (
  snapshot: CallState,
  incomingId?: string
): IncomingCallInfo | null => {
  if (incomingId) {
    const normalizedIncomingId = incomingId.trim();
    const fromIncoming = snapshot.incomingCalls.find(
      (call) =>
        call.id === incomingId ||
        matchesQueueUniqueId(call, normalizedIncomingId) ||
        collectIncomingCallCorrelators(call).includes(normalizedIncomingId)
    );
    if (fromIncoming) return fromIncoming;

    const fromQueue = snapshot.queueableCalls.find(
      (call) =>
        call.uniqueId === normalizedIncomingId ||
        matchesQueueUniqueId(call, normalizedIncomingId)
    );
    if (fromQueue) return fromQueue;
  }

  return snapshot.incomingCalls[0] ?? snapshot.queueableCalls[0] ?? null;
};
import type { UseConferenceControlRecoveryResult } from "~/context/call/useConferenceControlRecovery";

export type UseCallActionsParams = {
  state: CallState;
  setState: React.Dispatch<React.SetStateAction<CallState>>;
  updateState: (next: Partial<CallState>) => void;
  sipRef: React.RefObject<SipPhone | null>;
  socketRef: React.RefObject<AppSocket | null>;
  sipIdentityRef: React.MutableRefObject<{
    extension: string | null;
    sipUsername: string | null;
  }>;
  stateSnapshotRef: React.MutableRefObject<CallState>;
  pendingSuperviseRef: React.MutableRefObject<PendingSupervise | null>;
  pendingSuperviseTimeoutRef: React.MutableRefObject<number | null>;
  currentAgent: AgentPresence | null;
  user: {
    id?: string;
    name?: string | null;
    surname?: string | null;
    email?: string | null;
    extensionId?: string | null;
  } | null;
  ensureSipDomain: () => string | null | undefined;
  unhold: () => Promise<void>;
  reportError?: (message: string) => void;
  conferenceRecovery: UseConferenceControlRecoveryResult;
  canTakeCallsRef?: React.MutableRefObject<boolean>;
};

export type UseCallActionsResult = {
  ensureSipDomain: () => string | null | undefined;
  call: (target: string, options?: OutboundDialOptions) => Promise<void>;
  answer: (incomingId?: string) => Promise<void>;
  hangup: () => Promise<void>;
  sendDtmf: (tone: string) => Promise<void>;
  mute: () => void;
  supervise: (
    targetExtension: string,
    uniqueId: string,
    mode: SuperviseMode,
    options?: {
      customerId?: string | null;
      customerName?: string | null;
    }
  ) => Promise<void>;
  unmute: () => void;
  hold: () => Promise<void>;
  unhold: () => Promise<void>;
  swap: () => Promise<void>;
  transferToAgent: (agentId: string) => Promise<void>;
  transferBlind: (target: string) => Promise<void>;
  startConferenceWithAgents: (agentIds: string[]) => Promise<void>;
  joinConferenceRoom: (room: string) => Promise<void>;
  removeParticipant: (participantId: string) => void;
  dismissInvite: () => void;
  rejectPendingInvitation: (incomingId?: string) => Promise<void>;
  dismissCallTakenNotice: (noticeId: string) => void;
  dismissBargePromotionNotice: () => void;
  dismissBargeTransferNotice: () => void;
  clearCallError: () => void;
  canAcceptIncomingCall: (incomingId: string) => boolean;
  canAcceptQueueCall: (queueUniqueId: string) => boolean;
  resolveAnswerIncomingId: (queueUniqueId: string) => string;
  setCallMetadata: (meta: {
    customerId?: string | null;
    callUuid?: string | null;
    callId?: string | null;
    callReference?: string | null;
    callerNumber?: string | null;
    customerName?: string | null;
  }) => void;
  inviteOperator: (colleagueExtension: string, uniqueId: string) => Promise<void>;
};

export function useCallActions({
  state,
  setState,
  updateState,
  sipRef,
  socketRef,
  sipIdentityRef,
  stateSnapshotRef,
  pendingSuperviseRef,
  pendingSuperviseTimeoutRef,
  currentAgent,
  user,
  ensureSipDomain,
  unhold,
  reportError,
  conferenceRecovery,
  canTakeCallsRef,
}: UseCallActionsParams): UseCallActionsResult {
  const {
    armConferenceControlRecovery,
    scheduleConferenceNoAnswerRecovery,
    recoverOperatorCallControl,
  } = conferenceRecovery;

  const sipConnectLog = React.useCallback((): SipConnectLoggingContext | null => {
    return sipRef.current?.getConnectLoggingContext() ?? null;
  }, [sipRef]);

  const ensureSipReady = React.useCallback(() => {
    requireSipRegistered(
      stateSnapshotRef.current,
      sipConnectLog(),
      sipRef.current?.registrationState ?? null
    );
  }, [sipConnectLog, sipRef, stateSnapshotRef]);

  const call = React.useCallback(
    async (target: string, options?: OutboundDialOptions) => {
      ensureSipReady();
      const sipPhone = sipRef.current;
      if (!sipPhone) throw new Error("Phone is not initialized.");

      const snapshot = stateSnapshotRef.current;
      const sipDomain = ensureSipDomain();
      if (!sipDomain) throw new Error("Missing SIP domain.");
      const sipTarget = buildOutboundTarget(target);

      const onHeldInbound = isDialingFromHeldCall({
        isOnHold: snapshot.isOnHold,
        callStatus: snapshot.callStatus,
        hasHeldBackgroundSession: sipPhone.hasHeldBackgroundSession(),
        isForegroundSessionOnHold: sipPhone.isForegroundSessionOnHold(),
      });

      if (isConsultDialBlocked(snapshot.consultStatus) && sipPhone.hasHeldBackgroundSession()) {
        throw new Error("End or swap your current consult call before dialing another number.");
      }

      if (
        (snapshot.callStatus === "active" ||
          snapshot.callStatus === "dialing" ||
          snapshot.callStatus === "held") &&
        !onHeldInbound &&
        !sipPhone.hasHeldBackgroundSession()
      ) {
        throw new Error("Place the current call on hold before dialing out.");
      }

      if (
        onHeldInbound &&
        !sipPhone.hasHeldBackgroundSession() &&
        !sipPhone.isForegroundSessionOnHold()
      ) {
        await sipPhone.hold();
      }

      const backgroundHeldCallUuid =
        onHeldInbound && !sipPhone.isForegroundOutbound()
          ? (snapshot.callUuid ??
            snapshot.callId ??
            snapshot.backgroundHeldCallUuid ??
            null)
          : snapshot.backgroundHeldCallUuid;
      const backgroundHeldCallReference =
        onHeldInbound && !sipPhone.isForegroundOutbound()
          ? (snapshot.callReference ??
            snapshot.backgroundHeldCallReference ??
            null)
          : snapshot.backgroundHeldCallReference;
      const consultSlots = buildConsultSlotSnapshot(snapshot, sipTarget);

      updateState({
        callStatus: "dialing",
        callDirection: "outgoing",
        callerNumber: sipTarget,
        backgroundHeldCallUuid,
        backgroundHeldCallReference,
        heldCallerNumber: consultSlots.heldCallerNumber,
        consultNumber: consultSlots.consultNumber,
        consultStatus: consultSlots.consultStatus,
      });

      // Sidecar creates the OUTBOUND CallLog; attach the customer the operator dialled for.
      // A profile dial passes that customer in, because the state snapshot has not caught up
      // with it yet by the time the dial starts.
      const dialedAtMs = Date.now();
      const linkCustomerId = resolveOutboundLinkCustomerId({
        explicitCustomerId: options?.customerId,
        stateCustomerId: snapshot.customerId,
        dialingFromActiveCall: onHeldInbound,
      });
      if (linkCustomerId && shouldLinkOutboundCallLog(sipTarget)) {
        void linkRecentOutboundCallToCustomer({
          dialedNumber: sipTarget,
          customerId: linkCustomerId,
          dialedAtMs,
        })
          .then((uniqueId) => {
            if (!uniqueId) return;
            const current = stateSnapshotRef.current;
            if (current.callDirection !== "outgoing") return;
            if (current.callerNumber !== sipTarget) return;
            updateState({ callUuid: uniqueId, callId: uniqueId });
          })
          .catch(() => {});
      }

      try {
        await sipPhone.call(sipTarget, sipDomain);
      } catch (error) {
        const message = formatOutboundCallErrorMessage(error, sipTarget);
        reportError?.(message);
        if (onHeldInbound || sipPhone.hasHeldBackgroundSession()) {
          updateState({
            callStatus: "held",
            callDirection: "incoming",
            isOnHold: true,
            callerNumber: consultSlots.heldCallerNumber,
            callUuid: backgroundHeldCallUuid,
            callId: backgroundHeldCallUuid,
            backgroundHeldCallUuid,
            backgroundHeldCallReference,
            heldCallerNumber: consultSlots.heldCallerNumber,
            consultNumber: null,
            consultStatus: null,
          });
        } else {
          updateState({ callStatus: "idle", ...clearConsultSlotFields() });
        }
        throw error;
      }
    },
    [
      ensureSipDomain,
      ensureSipReady,
      reportError,
      sipRef,
      stateSnapshotRef,
      updateState,
    ]
  );

  const canTakeCalls = React.useCallback(() => {
    return canTakeCallsRef?.current ?? false;
  }, [canTakeCallsRef]);

  const answer = React.useCallback(
    async (incomingId?: string) => {
      if (!canTakeCalls()) {
        throw new Error(
          "Connection unavailable — wait until dashboard and SIP are both connected."
        );
      }
      const sipPhone = sipRef.current;
      if (!sipPhone) throw new Error("Phone is not initialized.");
      // INVITE can arrive (and ring) before React state flips to sipStatus "registered" —
      // allow answer if SIP.js already holds a pending invitation.
      const hasPendingInvite = incomingId
        ? sipPhone.hasPendingInvitation(incomingId)
        : sipPhone.hasIncomingInvitation();
      if (state.sipStatus !== "registered") {
        if (hasPendingInvite) {
          console.error(
            "[SIP] Answering while sipStatus is not yet registered (pending INVITE is present; likely REGISTER/transport vs UI state race).",
            {
              sipStatus: state.sipStatus,
              socketStatus: state.socketStatus,
            }
          );
        } else {
          ensureSipReady();
        }
      }
      const sipCtx = sipPhone.getConnectLoggingContext();
      if (sipCtx) {
        console.log("[SIP][accept] operator connect context", {
          ...sipCtx,
          extension: sipIdentityRef.current.extension,
          sipUsername: sipIdentityRef.current.sipUsername,
          incomingId: incomingId ?? null,
        });
      } else {
        console.warn(
          "[SIP][accept] no stored connect options (SIP may not have registered yet)"
        );
      }
      const waitForPendingInvitation = async () => {
        const deadline = Date.now() + 2000;
        while (
          !(incomingId
            ? sipPhone.hasPendingInvitation(incomingId)
            : sipPhone.hasIncomingInvitation()) &&
          Date.now() < deadline
        ) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 100));
        }
        const ready = incomingId
          ? sipPhone.hasPendingInvitation(incomingId)
          : sipPhone.hasIncomingInvitation();
        if (!ready) {
          throw new SipNotReadyError();
        }
      };

      try {
        await waitForPendingInvitation();
        await sipPhone.answer({ incomingId });
      } catch (error) {
        if (error instanceof SipNotReadyError) {
          updateState({ lastError: error.message });
          throw error;
        }
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Unable to answer call.";
        if (message.includes("No incoming SIP invitation")) {
          setState((prev) =>
            incomingId
              ? {
                  ...prev,
                  incomingCalls: prev.incomingCalls.filter(
                    (call) => call.id !== incomingId
                  ),
                }
              : prev
          );
          return;
        }
        updateState({ lastError: message });
        throw error;
      }

      const selected = resolveSelectedIncomingCall(
        stateSnapshotRef.current,
        incomingId
      );

      if (selected) {
        updateState({
          customerId: selected.customerId,
          callUuid: selected.callUuid,
          callId: selected.callId,
          callReference: selected.service,
          unitId: selected.unitId,
          callerNumber: selected.callerNumber,
          customerName: selected.customerName,
        });
        setState((prev) => {
          const selectedCorrelators = new Set(
            collectIncomingCallCorrelators(selected)
          );
          return {
            ...prev,
            incomingCalls: prev.incomingCalls.filter((call) => {
              const correlators = collectIncomingCallCorrelators(call);
              return !correlators.some((value) => selectedCorrelators.has(value));
            }),
          };
        });
        const ext = sipIdentityRef.current.extension;
        const uniqueId = selected.callUuid ?? selected.callId ?? selected.id;
        if (ext && uniqueId) void callAnswered(ext, uniqueId).catch(() => {});
      }
    },
    [canTakeCalls, sipConnectLog, sipIdentityRef, sipRef, state, stateSnapshotRef, updateState, setState]
  );

  const hangup = React.useCallback(async () => {
    const snapshot = stateSnapshotRef.current;
    const ext = sipIdentityRef.current.extension;
    const uniqueId = snapshot.callUuid ?? snapshot.callId ?? undefined;
    const sipPhone = sipRef.current;
    const hadHeldInboundParked = Boolean(sipPhone?.hasHeldBackgroundSession());
    const isEndingForegroundOutbound =
      hadHeldInboundParked &&
      (snapshot.callDirection === "outgoing" ||
        Boolean(sipPhone?.isForegroundOutbound()));

    const wasSuperviseSession =
      !!uniqueId &&
      snapshot.answeredCalls.some(
        (call) => call.uniqueId === uniqueId && !call.isMine
      );

    await sipPhone?.hangup();

    if (wasSuperviseSession) {
      updateState({
        callUuid: null,
        callerNumber: null,
        callReference: null,
        activeSuperviseMode: null,
      });
      updateState(clearConsultSlotFields());
      return;
    }

    if (isEndingForegroundOutbound) {
      updateState({
        callStatus: "held",
        callDirection: "incoming",
        isOnHold: true,
        callUuid:
          snapshot.backgroundHeldCallUuid ??
          snapshot.callUuid ??
          snapshot.callId ??
          null,
        callId: snapshot.backgroundHeldCallUuid ?? snapshot.callId ?? null,
        callReference:
          snapshot.backgroundHeldCallReference ?? snapshot.callReference ?? null,
        callerNumber:
          snapshot.heldCallerNumber ??
          snapshot.callerNumber ??
          null,
        consultNumber: null,
        consultStatus: null,
      });
      return;
    }

    if (ext && uniqueId) void callEnded(ext, uniqueId).catch(() => {});

    updateState(clearConsultSlotFields());
  }, [sipIdentityRef, sipRef, stateSnapshotRef, updateState]);

  const sendDtmf = React.useCallback(
    async (tone: string) => {
      if (state.callStatus !== "active" && state.callStatus !== "held") {
        throw new Error("DTMF is only available during an active call.");
      }
      await sipRef.current?.sendDtmf(tone);
    },
    [sipRef, state.callStatus]
  );

  const mute = React.useCallback(() => {
    sipRef.current?.mute();
    updateState({ isMuted: true });
  }, [sipRef, updateState]);

  const unmute = React.useCallback(() => {
    sipRef.current?.unmute();
    updateState({ isMuted: false });
  }, [sipRef, updateState]);

  const hold = React.useCallback(async () => {
    const snapshot = stateSnapshotRef.current;
    await sipRef.current?.hold();
    updateState({
      isOnHold: true,
      callStatus: "held",
      ...buildHoldSlotSnapshot(snapshot),
    });
  }, [sipRef, stateSnapshotRef, updateState]);

  const swap = React.useCallback(async () => {
    ensureSipReady();
    const sipPhone = sipRef.current;
    if (!sipPhone) throw new Error("Phone is not initialized.");
    if (!sipPhone.canSwapSessions()) {
      throw new Error("Both call legs must be active to swap.");
    }

    const snapshot = stateSnapshotRef.current;
    const swapResult = await sipPhone.swapSessions();

    updateState({
      ...buildSwappedConsultSlots(snapshot),
      callDirection: swapResult.foregroundDirection,
      consultStatus: snapshot.consultStatus,
    });
  }, [ensureSipReady, sipRef, stateSnapshotRef, updateState]);

  const resolveAgentTarget = React.useCallback(
    (agentId: string) => {
      const agent =
        state.agents.find((a) => a.id === agentId) ??
        state.agents.find((a) => a.sipExtension === agentId);
      if (!agent) throw new Error("Agent not found.");
      if (agent.status !== "online") throw new Error("Agent is not available.");
      const sipDomain = ensureSipDomain();
      if (!sipDomain) throw new Error("Missing SIP domain.");
      const target = agent.sipUri
        ? agent.sipUri
        : agent.sipExtension
          ? buildSipUri(agent.sipExtension, sipDomain)
          : buildSipUri(agentId, sipDomain);
      return target;
    },
    [ensureSipDomain, state.agents]
  );

  const transferToAgent = React.useCallback(
    async (agentId: string) => {
      ensureSipReady();
      const sipDomain = ensureSipDomain();
      if (!sipDomain) throw new Error("Missing SIP domain.");
      const target = resolveAgentTarget(agentId);
      await sipRef.current?.transferBlind(target, sipDomain);
    },
    [ensureSipDomain, resolveAgentTarget, sipConnectLog, sipRef, state]
  );

  const transferBlind = React.useCallback(
    async (target: string) => {
      ensureSipReady();
      const sipDomain = ensureSipDomain();
      if (!sipDomain) throw new Error("Missing SIP domain.");
      await sipRef.current?.transferBlind(target, sipDomain);
    },
    [ensureSipDomain, sipConnectLog, sipRef, state]
  );

  const startConferenceWithAgents = React.useCallback(
    async (agentIds: string[]) => {
      ensureSipReady();
      const sipDomain = ensureSipDomain();
      if (!sipDomain) throw new Error("Missing SIP domain.");
      if (state.callStatus !== "active") {
        throw new Error("No active call to add agents.");
      }
      const room =
        state.conferenceRoom ?? deriveConferenceRoom(state.callUuid);
      const currentParticipantsWithoutSelf = state.participants.filter(
        (p) => !currentAgent || p.id !== currentAgent.id
      );
      updateState({
        conferenceRoom: room,
        participants: [
          ...(currentAgent ? [currentAgent] : []),
          ...currentParticipantsWithoutSelf,
        ],
        callStatus: "dialing",
        callDirection: "outgoing",
        isOnHold: false,
      });
      armConferenceControlRecovery();
      scheduleConferenceNoAnswerRecovery(room);
      try {
        await sipRef.current?.transferCurrentCallToRoom(room, sipDomain);
        await sipRef.current?.joinRoom(room, sipDomain);
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Unable to start conference.";
        updateState({ lastError: message, isOnHold: false });
        throw error;
      }
      socketRef.current?.inviteConference({
        agentIds,
        room,
        fromAgentId: user?.id,
        callUuid: state.callUuid ?? undefined,
        callerNumber: state.callerNumber ?? undefined,
      });
    },
    [
      armConferenceControlRecovery,
      currentAgent,
      ensureSipDomain,
      scheduleConferenceNoAnswerRecovery,
      sipRef,
      socketRef,
      state,
      updateState,
      sipConnectLog,
      user?.id,
    ]
  );

  const joinConferenceRoom = React.useCallback(
    async (room: string) => {
      ensureSipReady();
      const sipDomain = ensureSipDomain();
      if (!sipDomain) throw new Error("Missing SIP domain.");
      updateState({
        conferenceRoom: room,
        participants: currentAgent ? [currentAgent] : [],
      });
      await sipRef.current?.joinRoom(room, sipDomain);
    },
    [currentAgent, ensureSipDomain, sipConnectLog, sipRef, state, updateState]
  );

  const removeParticipant = React.useCallback(
    (participantId: string) => {
      if (!participantId) return;
      setState((prev) => {
        const filtered = prev.participants.filter(
          (p) => p.id !== participantId
        );
        if (filtered.length === prev.participants.length) return prev;
        const participants = currentAgent
          ? filtered.some((p) => p.id === currentAgent.id)
            ? filtered
            : [currentAgent, ...filtered]
          : filtered;
        return { ...prev, participants };
      });
      armConferenceControlRecovery();
      void recoverOperatorCallControl();
      window.setTimeout(() => void recoverOperatorCallControl(), 1200);
      window.setTimeout(() => void recoverOperatorCallControl(), 3000);
    },
    [
      armConferenceControlRecovery,
      currentAgent,
      recoverOperatorCallControl,
      setState,
    ]
  );

  const dismissInvite = React.useCallback(
    () => updateState({ pendingInvite: null }),
    [updateState]
  );

  const rejectPendingInvitation = React.useCallback(
    async (incomingId?: string) => {
      await sipRef.current?.rejectPendingInvitation(incomingId);
    },
    [sipRef]
  );

  const canAcceptIncomingCall = React.useCallback(
    (incomingId: string) => {
      if (!canTakeCalls()) return false;
      return sipRef.current?.hasPendingInvitation(incomingId) ?? false;
    },
    [canTakeCalls, sipRef]
  );

  const resolveAnswerIncomingId = React.useCallback(
    (queueUniqueId: string) => {
      const sipPhone = sipRef.current;
      const normalizedUniqueId = queueUniqueId.trim();
      if (!sipPhone || !normalizedUniqueId) return normalizedUniqueId;

      if (sipPhone.hasPendingInvitation(normalizedUniqueId)) {
        return normalizedUniqueId;
      }

      const snapshot = stateSnapshotRef.current;
      for (const call of snapshot.incomingCalls) {
        if (!matchesQueueUniqueId(call, normalizedUniqueId)) continue;
        for (const correlator of collectIncomingCallCorrelators(call)) {
          if (sipPhone.hasPendingInvitation(correlator)) return correlator;
        }
        return call.id;
      }

      const queueCall = snapshot.queueableCalls.find(
        (call) => call.uniqueId === normalizedUniqueId
      );
      if (queueCall?.callerNumber) {
        const matchedIncoming = snapshot.incomingCalls.find(
          (call) => call.callerNumber === queueCall.callerNumber
        );
        if (matchedIncoming) {
          for (const correlator of collectIncomingCallCorrelators(matchedIncoming)) {
            if (sipPhone.hasPendingInvitation(correlator)) return correlator;
          }
          return matchedIncoming.id;
        }
      }

      return normalizedUniqueId;
    },
    [sipRef, stateSnapshotRef]
  );

  const canAcceptQueueCall = React.useCallback(
    (queueUniqueId: string) => {
      if (!canTakeCalls()) return false;

      const sipPhone = sipRef.current;
      const normalizedUniqueId = queueUniqueId.trim();
      if (!sipPhone || !normalizedUniqueId) return false;

      const resolvedIncomingId = resolveAnswerIncomingId(normalizedUniqueId);
      if (sipPhone.hasPendingInvitation(resolvedIncomingId)) return true;

      const claimStatus =
        stateSnapshotRef.current.queueClaimByUniqueId[normalizedUniqueId];
      return (
        claimStatus === "claimed-by-me" && sipPhone.hasIncomingInvitation()
      );
    },
    [canTakeCalls, resolveAnswerIncomingId, sipRef, stateSnapshotRef]
  );

  const dismissCallTakenNotice = React.useCallback(
    (noticeId: string) => {
      setState((prev) => {
        const nextNotices = prev.callTakenNotices.filter(
          (notice) => notice.id !== noticeId
        );
        if (nextNotices.length === prev.callTakenNotices.length) return prev;
        return { ...prev, callTakenNotices: nextNotices };
      });
    },
    [setState]
  );

  const dismissBargePromotionNotice = React.useCallback(() => {
    updateState({ bargePromotionNotice: null });
  }, [updateState]);

  const clearCallError = React.useCallback(() => {
    updateState({ lastError: null });
  }, [updateState]);

  const setCallMetadata = React.useCallback(
    (meta: {
      customerId?: string | null;
      callUuid?: string | null;
      callId?: string | null;
      callReference?: string | null;
      unitId?: string | null;
      callerNumber?: string | null;
      customerName?: string | null;
    }) => {
      setState((prev) => {
        let hasChange = false;
        for (const key of Object.keys(meta) as (keyof typeof meta)[]) {
          const value = meta[key];
          if (value === undefined) continue;
          if (prev[key as keyof CallState] !== value) {
            hasChange = true;
            break;
          }
        }
        if (!hasChange) return prev;
        return { ...prev, ...meta };
      });
    },
    [setState]
  );

  const supervise = React.useCallback(
    async (
      targetExtension: string,
      uniqueId: string,
      mode: SuperviseMode,
      options?: {
        customerId?: string | null;
        customerName?: string | null;
      }
    ) => {
      const myExtension = sipIdentityRef.current.extension;
      if (!myExtension) throw new Error("No SIP extension configured.");

      const answeredCallsSnapshot = stateSnapshotRef.current.answeredCalls;
      const targetCall = answeredCallsSnapshot.find(
        (c) => c.uniqueId === uniqueId
      );

      if (pendingSuperviseTimeoutRef.current != null) {
        window.clearTimeout(pendingSuperviseTimeoutRef.current);
      }

      preparePendingSupervise(
        pendingSuperviseRef,
        pendingSuperviseTimeoutRef,
        mode
      );

      updateState({
        callUuid: uniqueId,
        callerNumber: targetCall?.caller ?? null,
        callReference: targetCall?.callType ?? null,
        customerId: options?.customerId ?? null,
        customerName: options?.customerName ?? null,
        activeSuperviseMode: mode,
      });

      try {
        await superviseApi({
          supervisorExtension: myExtension,
          targetExtension,
          uniqueId,
          mode,
        });
      } catch (error) {
        pendingSuperviseRef.current = null;
        if (pendingSuperviseTimeoutRef.current != null) {
          window.clearTimeout(pendingSuperviseTimeoutRef.current);
          pendingSuperviseTimeoutRef.current = null;
        }
        updateState({
          callUuid: null,
          callerNumber: null,
          callReference: null,
          customerId: null,
          customerName: null,
          activeSuperviseMode: null,
        });
        throw error;
      }
    },
    [
      pendingSuperviseRef,
      pendingSuperviseTimeoutRef,
      sipIdentityRef,
      updateState,
    ]
  );

  const pendingBargeTransferTimeoutRef = React.useRef<number | null>(null);

  const clearPendingBargeTransferTimeout = React.useCallback(() => {
    if (pendingBargeTransferTimeoutRef.current != null) {
      window.clearTimeout(pendingBargeTransferTimeoutRef.current);
      pendingBargeTransferTimeoutRef.current = null;
    }
  }, []);

  const dismissBargeTransferNotice = React.useCallback(() => {
    updateState({ bargeTransferNotice: null });
  }, [updateState]);

  const inviteOperator = React.useCallback(
    async (colleagueExtension: string, uniqueId: string) => {
      const myExtension = sipIdentityRef.current.extension;
      if (!myExtension) throw new Error("No SIP extension configured.");

      await superviseApi({
        supervisorExtension: colleagueExtension,
        targetExtension: myExtension,
        uniqueId,
        mode: "barge",
      });

      clearPendingBargeTransferTimeout();
      updateState({
        pendingBargeTransfer: {
          uniqueId,
          invitedExtension: colleagueExtension,
          atMs: Date.now(),
        },
        lastError: null,
      });

      pendingBargeTransferTimeoutRef.current = window.setTimeout(() => {
        if (stateSnapshotRef.current.pendingBargeTransfer?.uniqueId === uniqueId) {
          updateState({ pendingBargeTransfer: null });
        }
        pendingBargeTransferTimeoutRef.current = null;
      }, PENDING_BARGE_TRANSFER_WINDOW_MS);
    },
    [clearPendingBargeTransferTimeout, sipIdentityRef, stateSnapshotRef, updateState]
  );

  return {
    ensureSipDomain,
    call,
    answer,
    hangup,
    sendDtmf,
    mute,
    unmute,
    hold,
    unhold,
    swap,
    transferToAgent,
    transferBlind,
    startConferenceWithAgents,
    joinConferenceRoom,
    removeParticipant,
    dismissInvite,
    rejectPendingInvitation,
    dismissCallTakenNotice,
    dismissBargePromotionNotice,
    dismissBargeTransferNotice,
    clearCallError,
    canAcceptIncomingCall,
    canAcceptQueueCall,
    resolveAnswerIncomingId,
    setCallMetadata,
    supervise,
    inviteOperator,
  };
}
